import { useCallback, useEffect, useState } from 'react';
import { ArrowRightLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { declarerActif, getImpact, simulerMigration } from '@/shared/api/bridge';
import { estMigrationEnAttente, pluriel, resumerMigration } from '@/features/bridge/bridge-etat';
import ConfirmationImpact from '@/features/bridge/components/ConfirmationImpact';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

const APERCU = 40;

/**
 * La bascule du périmètre, du client vers l'actif : un APERÇU depuis le 07/10/2026.
 *
 * POURQUOI CE BANDEAU EXISTE
 *
 * #217 a fait de la DÉCLARATION la seule source du périmètre, et #218 y a branché les
 * revenus. Mesure du 28/09 : 159 clients rattachés en staging, 0 actif déclaré, des
 * écrans de revenus vides. Ce bandeau dit ce qui reste à déclarer.
 *
 * PLUS DE « JOUER LA MIGRATION »
 *
 * Une déclaration est définitive (07/10). Le bouton qui déclarait tout le plan d'un
 * clic aurait verrouillé 252 actifs de staging, dont des actifs sans mandat, chez
 * l'agrégateur de leur fiche, sans retour possible. Le serveur répond désormais 410 à
 * ce geste. Le plan se lit ici, et chaque ligne se déclare SEULE, après le dialogue
 * d'impact qui rappelle que c'est définitif.
 *
 * CE QU'IL NE FAIT PAS
 *
 * Il n'apparaît pas quand il n'y a rien à déclarer : un bandeau permanent apprend à ne
 * plus le lire.
 */
export default function BandeauMigration({ surMigration }) {
  const [simulation, setSimulation] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [detail, setDetail] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const simuler = useCallback(async () => {
    setErreur(null);
    try {
      setSimulation(await simulerMigration());
    } catch (exc) {
      setErreur(exc);
    }
  }, []);

  useEffect(() => { simuler(); }, [simuler]);

  const demander = async (ligne) => {
    setEnCours(true);
    setErreur(null);
    try {
      const impact = await getImpact(ligne.asset_id, ligne.agregateur, false);
      setConfirmation({ titre: `Déclarer ${ligne.type} (${ligne.client || 'client'}) à ${ligne.agregateur}`,
                        impact, ligne });
    } catch (exc) {
      setErreur(exc);
    } finally {
      setEnCours(false);
    }
  };

  const declarer = async () => {
    setEnCours(true);
    try {
      await declarerActif(confirmation.ligne.agregateur, { asset_id: confirmation.ligne.asset_id });
      setConfirmation(null);
      await simuler();
      surMigration?.();
    } catch (exc) {
      setErreur(exc);
      setConfirmation(null);
    } finally {
      setEnCours(false);
    }
  };

  if (erreur) return <ErreurBloc erreur={erreur} testid="migration-erreur" />;
  if (!estMigrationEnAttente(simulation)) return null;

  const { parAgregateur, ignores } = resumerMigration(simulation);
  const lignes = simulation.declarations || [];

  return (
    <section className="rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3.5"
             aria-labelledby="migration-titre" data-testid="migration-bandeau">
      <div className="flex flex-wrap items-start gap-3">
        <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
        <div className="min-w-[260px] flex-1 space-y-1">
          <h2 id="migration-titre" className="text-[14px] font-semibold text-amber-950">
            À déclarer : {pluriel(simulation.clients_attribues, 'client rattaché', 'clients rattachés')},
            {' '}{pluriel(simulation.a_declarer, 'actif pas encore déclaré', 'actifs pas encore déclarés')}
          </h2>
          <p className="max-w-[70ch] text-[13px] leading-relaxed text-amber-900">
            Un actif ne compte au revenu et au pilotage que s&apos;il est déclaré à son
            agrégateur. Une déclaration est définitive : elle se fait actif par actif.
          </p>
          <p className="text-[12.5px] tabular-nums text-amber-900"
             data-testid="migration-chiffres">
            {parAgregateur.map(([nom, n]) => `${nom} ${n}`).join(' · ')}
            {simulation.sautes > 0 && ` · ${simulation.sautes} ignorés : `
              + ignores.map(([motif, n]) => `${n} ${motif}`).join(', ')}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setDetail((d) => !d)}
                aria-expanded={detail} data-testid="migration-detail-btn">
          {detail ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
          Voir le plan
        </Button>
      </div>

      {detail && (
        <div className="mt-3 max-h-72 overflow-auto rounded-md border border-amber-200 bg-white"
             data-testid="migration-plan">
          <table className="w-full min-w-[420px] text-[12.5px]">
            <thead className="sticky top-0 bg-neutral-50 text-left text-neutral-500">
              <tr><th className="px-3 py-1.5 font-normal">Client</th>
                <th className="font-normal">Actif</th>
                <th className="font-normal">Agrégateur</th>
                <th className="font-normal">Mandat</th>
                <th className="w-24" /></tr>
            </thead>
            <tbody>
              {lignes.slice(0, APERCU).map((l) => (
                <tr key={`${l.agregateur}-${l.asset_id}`} className="border-t border-neutral-100">
                  <td className="px-3 py-1.5">{l.client || '—'}</td>
                  <td>{l.type}{l.reference_chantier ? ` · ${l.reference_chantier}` : ''}</td>
                  <td>{l.agregateur}</td>
                  <td className={l.statut_mandat === 'actif' ? 'text-emerald-700' : 'text-amber-700'}>
                    {l.statut_mandat === 'actif' ? 'actif' : (l.statut_mandat || 'aucun')}
                  </td>
                  <td className="pr-3 text-right">
                    <button type="button" disabled={enCours} onClick={() => demander(l)}
                            className="text-[12px] underline underline-offset-2 disabled:opacity-50"
                            data-testid={`migration-declarer-${l.asset_id}`}>
                      Déclarer…
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {lignes.length > APERCU && (
            <p className="border-t border-neutral-100 px-3 py-2 text-[12px] text-neutral-500">
              {APERCU} premières lignes sur {lignes.length}.
            </p>
          )}
        </div>
      )}

      <ConfirmationImpact prefixe="migration" confirmation={confirmation} enCours={enCours}
                          onAnnuler={() => setConfirmation(null)} onConfirmer={declarer} />
    </section>
  );
}
